import React, { useCallback, useState, useEffect, useMemo } from 'react';
import { InformationCircleOutline } from 'antd-mobile-icons';
import { Popup, Checkbox, Button } from 'antd-mobile';
import type { ConfirmationProductPriceInfo_, ConfirmationProduct_, CartUsableCoupon_1 } from '@/apis/orderConfirm.type';
import { right_arrow_icon } from '@/constants';
import { ProductCardCountStepper } from '@/pages/Product/Menu/List/Card/NumButton';
import { PackingFeeListPopupIcon } from '@/pages/Product/Cart/PackingFeeList';
import Coupon from '../Coupon';
import { isDouyinAppChannel } from '@/channels';
import './index.less';

interface IProps {
  data?: ConfirmationProductPriceInfo_;
  payTotalPrice: number;
  tablewareCode: string;
  orderType: number;
  /** add to cart */
  onAdd?: (p: ConfirmationProduct_) => void;
  /** remove */
  onSub?: (p: ConfirmationProduct_) => void;
  onOrderCouponChange?: (list?: CartUsableCoupon_1[]) => void;
  onFreightCouponPromotionIdChange?: (code?: string) => void;
}

const ProductList: React.FC<IProps> = (props) => {
  const {
    data,
    payTotalPrice,
    tablewareCode,
    orderType,
    onAdd,
    onSub,
    onOrderCouponChange,
    onFreightCouponPromotionIdChange,
  } = props;
  const {
    cartProductList,
    realProductTotalPrice,
    realTotalAmount,
    discountAmount,
    tablewarePrice,
    realDeliveryPrice,
    deliveryPrice,
    title,
    productCouponInfo,
    orderCouponInfo,
    freightCouponInfo,
    serviceDescription,
    serviceDescriptionUrl,
    packingFeeTotalPrice,
    realPackingFeeTotalPrice,
    packingFee,
  } = data || {};

  const [visible, setVisible] = useState<boolean>(false);
  const [innerCouponList, setInnerCouponList] = useState<CartUsableCoupon_1[]>([]);

  const [freightVisible, setFreightVisible] = useState<boolean>(false);
  const [innerFreightCouponPromotionId, setInnerFreightCouponPromotionId] = useState<string>();

  const selectedOrderCoupon = useMemo(() => {
    return (orderCouponInfo?.couponList || []).find(({ isSelect }) => isSelect);
  }, [orderCouponInfo]);

  const selectedFreightCoupon = useMemo(() => {
    return (freightCouponInfo?.couponList || []).find(({ isSelect }) => isSelect);
  }, [freightCouponInfo]);

  useEffect(() => {
    visible && setInnerCouponList(orderCouponInfo?.couponList || []);
  }, [orderCouponInfo, visible]);

  useEffect(() => {
    freightVisible && setInnerFreightCouponPromotionId(selectedFreightCoupon?.promotionId);
  }, [selectedFreightCoupon, freightVisible]);

  // 操作 含有2代表可修改商品数量
  const canAddOrSub = useCallback((actions?: number[]) => (actions || []).includes(2), []);

  const calcDesc = useCallback((type: number) => {
    if (type === 1) {
      return '特价';
    }
    if (type === 3) {
      return '立减';
    }
  }, []);

  const showFreightCouponModal = useMemo(() => Number(freightCouponInfo?.usableNumber) > 0, [freightCouponInfo]);
  const showPackingFee = useMemo(
    () =>
      Number(realPackingFeeTotalPrice) > 0 ||
      (Number(packingFeeTotalPrice) > 0 && Number(realPackingFeeTotalPrice) === 0),
    [packingFeeTotalPrice, realPackingFeeTotalPrice],
  );

  if (!data) return null;

  return (
    <div className="product-list-conatiner">
      <div className="title">{title || '餐品详情'}</div>
      <div className="product-content">
        {(cartProductList || [])
          // saleStatus 1可售
          .filter(({ saleStatus }) => saleStatus === 1)
          .map((i) => {
            const {
              uniqueKey,
              productImage,
              productName,
              quantity,
              subtotal,
              realSubtotal,
              comboItemList,
              actions,
              couponList,
            } = i || {};
            return (
              <div className="item" key={uniqueKey}>
                <img className="product-img" src={productImage} />
                <div className="detail">
                  <div className="name">{productName}</div>
                  <div className="coupon-content">
                    {(couponList || []).map((couponItem) => {
                      const { couponId, couponName, showPmtType, moduleTitle, eligibleItemQuantity } = couponItem || {};
                      return (
                        <div key={couponId} className="coupon-item">
                          {showPmtType === 3 ? (
                            <>
                              <span className="module-title">{moduleTitle}</span>
                              <span className="coupon-name">{couponName}</span>
                            </>
                          ) : (
                            <>
                              <span className="symbol">{showPmtType === 2 ? '促' : '券'}</span>
                              <span className="coupon-name-wrapper">
                                <span>{couponName}</span>
                              </span>
                              <img
                                src="https://img.mcd.cn/mini/main/images/coupon_right.png"
                                className="after-bg"></img>
                            </>
                          )}
                          {(showPmtType === 2 || showPmtType === 3) &&
                          Number(eligibleItemQuantity) > 0 &&
                          Number(eligibleItemQuantity) < quantity ? (
                            <span className="smart-pricing-bg">{`${Number(eligibleItemQuantity)}件享受优惠`}</span>
                          ) : null}
                        </div>
                      );
                    })}
                  </div>

                  <div className="comboItems">
                    {(comboItemList || []).map((comboItem, idx) => {
                      const { quantity: comboQuantity, name } = comboItem || {};
                      return (
                        <div key={`${name}-${idx}`} className="combo-item">
                          {comboQuantity} x {name}
                        </div>
                      );
                    })}
                  </div>

                  <div className="price-wrapper">
                    {canAddOrSub(actions) ? (
                      <ProductCardCountStepper
                        count={quantity}
                        minCount={0}
                        onAdd={() => {
                          onAdd && onAdd(i);
                        }}
                        onSub={() => {
                          onSub && onSub(i);
                        }}
                      />
                    ) : (
                      <span>{quantity}份</span>
                    )}

                    <div className="price-content">
                      {subtotal !== realSubtotal && (
                        <span className="market">
                          <span className="ui-symbol">¥</span>
                          <span className="ui-val">{subtotal}</span>
                        </span>
                      )}
                      <span className="real">
                        <span className="ui-symbol">¥</span>
                        <span className="ui-val">{realSubtotal}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
      </div>

      <div className="total-price">
        <span className="label">商品小计</span>
        <span className="val">
          <span className="ui-symbol">¥</span>
          <span className="ui-val">{realProductTotalPrice}</span>
        </span>
      </div>
      {tablewareCode === 'yes' && orderType === 1 && (
        <div className="tableware-price">
          <span className="label">餐具费</span>
          <span className="val">
            <span className="ui-symbol">¥</span>
            <span className="ui-val">{tablewarePrice}</span>
          </span>
        </div>
      )}
      {orderType === 2 &&
        (freightCouponInfo && freightCouponInfo.couponList && freightCouponInfo.couponList.length > 0 ? (
          <div
            className="delivery-price"
            onClick={() => {
              setFreightVisible(true);
            }}>
            <div className="label">
              <span>{isDouyinAppChannel() ? '配送费+小料' : '配送费'}</span>
              {selectedFreightCoupon && (
                <div className="coupon-title">
                  {selectedFreightCoupon?.showPmtType === 3 ? (
                    <>
                      <span className="symbol-omm">{selectedFreightCoupon?.angleMark}</span>
                      <span className="val-omm">{selectedFreightCoupon?.title}</span>
                    </>
                  ) : (
                    <>
                      <span className="symbol">促</span>
                      <span className="val">{selectedFreightCoupon?.title}</span>
                    </>
                  )}
                </div>
              )}
              {freightCouponInfo?.discountTips && <span className="tips">{freightCouponInfo?.discountTips}</span>}
            </div>
            <span className="val">
              {!selectedFreightCoupon?.promotionId ? (
                showFreightCouponModal && <Coupon text={`${freightCouponInfo?.usableNumber || 0}张可用`} />
              ) : (
                <span className="market-price">
                  <span className="symbol ui-symbol">¥</span>
                  <span className="price ui-val">{deliveryPrice}</span>
                </span>
              )}
              <div className="real-price">
                <span className="symbol ui-symbol">¥</span>
                <span className="price ui-val">{realDeliveryPrice}</span>
              </div>
              <img className="arrow" src={right_arrow_icon} />
            </span>
          </div>
        ) : (
          <div className="delivery-price">
            <div className="label">
              <span>{isDouyinAppChannel() ? '配送费+小料' : '配送费'}</span>
            </div>
            <span className="val">
              <span className="symbol ui-symbol">¥</span>
              <span className="price ui-val">{realDeliveryPrice || 0}</span>
            </span>
          </div>
        ))}

      {showPackingFee && (
        <div className="packing-fee_total">
          <span className="label">
            打包服务费
            <PackingFeeListPopupIcon
              className="packing-fee_total_info_icon"
              popupStyle={{ zIndex: 999 }}
              totalPackingFee={Number(realPackingFeeTotalPrice) * 100}
              products={cartProductList}
              discounts={packingFee?.discounts}
            />
          </span>
          <div className="val">
            {packingFeeTotalPrice !== realPackingFeeTotalPrice && (
              <span className="packing-fee_total_original_price">
                <small>¥</small>
                <span>{packingFeeTotalPrice}</span>
              </span>
            )}
            <span className="symbol ui-symbol">¥</span>
            <span className="price ui-val">{realPackingFeeTotalPrice}</span>
          </div>
        </div>
      )}
      {orderCouponInfo &&
      ((orderCouponInfo?.usedCount === 0 && orderCouponInfo?.usableNumber) ||
        Number(orderCouponInfo?.usedDiscountAmount)) ? (
        <div
          className="order-coupon-info"
          onClick={() => {
            setVisible(true);
          }}>
          <div className="label">
            <span>订单优惠</span>
            {selectedOrderCoupon && (
              <div className="coupon-title">
                {selectedOrderCoupon?.showPmtType === 3 ? (
                  <>
                    <span className="symbol-omm">{selectedOrderCoupon?.angleMark}</span>
                    <span className="val-omm">{selectedOrderCoupon?.title}</span>
                  </>
                ) : (
                  <>
                    <span className="symbol">券</span>
                    <span className="val">{selectedOrderCoupon?.title}</span>
                  </>
                )}
              </div>
            )}
            {orderCouponInfo?.discountTips && <span className="tips">{orderCouponInfo?.discountTips}</span>}
          </div>
          <span className="val">
            {orderCouponInfo?.usedCount === 0 && orderCouponInfo?.usableNumber ? (
              <Coupon text={`${orderCouponInfo?.usableNumber}张可用`} />
            ) : (
              <>
                <span className="symbol ui-symbol">-¥</span>
                <span className="price ui-val">{orderCouponInfo?.usedDiscountAmount || 0}</span>
              </>
            )}

            <img className="arrow" src={right_arrow_icon} />
          </span>
        </div>
      ) : null}

      {productCouponInfo && productCouponInfo?.usedCount ? (
        <div className="product-coupon-info">
          <span className="label">
            商品优惠 <span className="desc">已用优惠{productCouponInfo?.usedCount}张</span>
          </span>
          <span className="val">
            <span className="symbol ui-symbol">-¥</span>
            <span className="price ui-val">{productCouponInfo?.usedDiscountAmount}</span>
          </span>
        </div>
      ) : null}
      <div className="division">
        <div className="line" />
        <span className="left" />
        <span className="right" />
      </div>
      <div className="real-total-price">
        {Number(discountAmount) ? (
          <div className="discountAmount-wrapper">
            已优惠
            <span className="val">
              <span className="ui-symbol">¥</span>
              <span className="price ui-val">{discountAmount}</span>
            </span>
          </div>
        ) : null}
        <div className="realTotalPrice-wrapper">
          合计
          <span className="val">
            <span className="symbol ui-symbol">¥</span>
            <span className="price ui-val total">{String(payTotalPrice)}</span>
          </span>
        </div>
      </div>
      <div className="legal-wrapper">
        <InformationCircleOutline />
        <span className="legal-line" onClick={() => (window.location.href = serviceDescriptionUrl as string)}>
          {serviceDescription}
        </span>
      </div>

      <Popup
        visible={visible}
        onMaskClick={() => {
          setVisible(false);
        }}
        bodyStyle={{ height: 'max-content', maxHeight: '80%', overflowY: 'scroll' }}>
        <div className="order-coupon-popup-content">
          <div className="title">{'订单优惠'}</div>
          <div className="available-coupon">
            您有<span className="num">{orderCouponInfo?.usableNumber}</span>张优惠券可用
          </div>
          <div className="order-coupon-container">
            {innerCouponList.map((i, idx) => {
              const {
                code,
                canSelect,
                id,
                discountType,
                discountUnit,
                discountValue,
                isSelect,
                title: couponTitle,
                currentDayAvailableCount,
                totalCount,
                showPmtType,
              } = i || {};
              return (
                <Checkbox
                  key={idx}
                  checked={isSelect}
                  onChange={(bool: boolean) => {
                    const newInnerCouponList = (innerCouponList || []).map((item) =>
                      item === i ? { ...item, isSelect: bool } : { ...item, isSelect: false },
                    );
                    setInnerCouponList(newInnerCouponList);
                  }}
                  className="item"
                  disabled={!canSelect}
                  value={code}
                  style={{
                    '--font-size': '14px',
                  }}>
                  <div className="coupon-wrapper">
                    <div className="pattern">
                      <div className="detail">
                        <span className="desc">{calcDesc(discountType!)}</span>
                        <div className="val-wrapper">
                          <span className="val">{discountValue}</span>
                          <span className="unit">{discountUnit}</span>
                        </div>
                      </div>
                    </div>
                    <div className="info">
                      <div className="title">{couponTitle}</div>
                      {totalCount && currentDayAvailableCount ? (
                        <div className="count">
                          今日{currentDayAvailableCount}次，总共{totalCount}次
                        </div>
                      ) : null}
                    </div>
                    {showPmtType === 3 && <span className="tag">O麦金会员</span>}
                  </div>
                </Checkbox>
              );
            })}
          </div>
          <div className="btn-wrapper">
            <Button
              color="primary"
              shape="rounded"
              size="middle"
              onClick={() => {
                setVisible(false);
                onOrderCouponChange && onOrderCouponChange(innerCouponList);
              }}>
              确定
            </Button>
          </div>
        </div>
      </Popup>

      <Popup
        visible={freightVisible}
        onMaskClick={() => {
          setFreightVisible(false);
        }}
        bodyStyle={{ height: 'max-content', maxHeight: '80%', overflowY: 'scroll' }}>
        <div className="freight-coupon-popup-content">
          <div className="title">{'配送费订单优惠'}</div>
          <div className="available-coupon">
            您有<span className="num">{freightCouponInfo?.usableNumber}</span>张优惠券可用
          </div>
          <div className="freight-coupon-container">
            {(freightCouponInfo?.couponList || []).map((i) => {
              const {
                promotionId,
                canSelect,
                id,
                discountType,
                discountUnit,
                discountValue,
                isSelect,
                title: couponTitle,
                currentDayAvailableCount,
                totalCount,
                showPmtType,
              } = i || {};
              return (
                <Checkbox
                  key={promotionId}
                  checked={promotionId === innerFreightCouponPromotionId}
                  onChange={(bool: boolean) => {
                    setInnerFreightCouponPromotionId(bool ? promotionId : '');
                  }}
                  className="item"
                  disabled={!canSelect}
                  value={promotionId}
                  style={{
                    '--font-size': '14px',
                  }}>
                  <div className="coupon-wrapper">
                    <div className="pattern">
                      <div className="detail">
                        <span className="desc">{calcDesc(discountType!)}</span>
                        <div className="val-wrapper">
                          <span className="val">{discountValue}</span>
                          <span className="unit">{discountUnit}</span>
                        </div>
                      </div>
                    </div>
                    <div className="info">
                      <div className="title">{couponTitle}</div>
                      {totalCount && currentDayAvailableCount ? (
                        <div className="count">
                          今日{currentDayAvailableCount}次，总共{totalCount}次
                        </div>
                      ) : null}
                    </div>
                    {showPmtType === 3 && <span className="tag">O麦金会员</span>}
                  </div>
                </Checkbox>
              );
            })}
          </div>
          <div className="btn-wrapper">
            <Button
              color="primary"
              shape="rounded"
              size="middle"
              onClick={() => {
                setFreightVisible(false);
                onFreightCouponPromotionIdChange && onFreightCouponPromotionIdChange(innerFreightCouponPromotionId);
              }}>
              确定
            </Button>
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default ProductList;
